import { useQuery } from "@tanstack/react-query";
import { Lightbulb, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { api, ApiError } from "../../shared/api/client";
import { useDebounced } from "../../shared/hooks/useDebounced";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
  cn,
} from "../../shared/ui";
import type {
  PageResponse,
  ProjectSummary,
  WorkSuggestion,
  WorklogView,
} from "../../shared/types/api";
import { formatDayLong, formatMinutes, parseDuration } from "./timesheetVocabulary";

const INTERNAL = "__internal__";

/**
 * Logs or corrects one entry of time (§68, §69).
 *
 * <p>The duration field takes "2h 30m", "2:30", "150m" or "2.5" and shows what it understood
 * underneath. §69 stores minutes, so the parse is exact — but a person who typed "2h30" and got
 * a rejection with no explanation would simply learn to distrust the field.
 *
 * <p>§73's suggestions sit above the form, and carry no hours. That absence is the point: a
 * pre-filled figure is one somebody confirms without reading, which is precisely the timesheet
 * that is not worth collecting.
 */
export function LogTimeDrawer({
  open,
  date,
  entry,
  onClose,
  onSaved,
}: {
  open: boolean;
  date: string | null;
  entry: WorklogView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = entry != null;

  const [workDate, setWorkDate] = useState("");
  const [duration, setDuration] = useState("");
  const [target, setTarget] = useState("");
  const [workItemId, setWorkItemId] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);
  /* Filling a week means five or six entries in a row. Reopening the panel between each one is
     the difference between a minute and five, so it can stay open and keep the date. */
  const [logAnother, setLogAnother] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const minutes = parseDuration(duration);

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    enabled: open && !editing,
  });

  const searchBox = useRef<HTMLInputElement>(null);
  const debounced = useDebounced(search, 200);

  const suggestions = useQuery({
    // Suggestions when the box is empty, matches when it is not — one list either way.
    queryKey: ["worklog-suggestions", debounced],
    queryFn: () =>
      api.get<WorkSuggestion[]>(
        "/api/v1/worklogs/suggestions" + (debounced ? `?q=${encodeURIComponent(debounced)}` : ""),
      ),
    enabled: open && !editing,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setWorkDate(entry?.workDate ?? date ?? "");
    setDuration(entry ? formatMinutes(entry.minutes) : "");
    setTarget(entry?.projectId ?? "");
    setWorkItemId(entry?.workItemId ?? null);
    setDescription(entry?.description ?? "");
    setSearch("");
    setPickedLabel(entry?.workItemKey ? `${entry.workItemKey} · ${entry.workItemTitle}` : null);
    setLogAnother(false);
    setError(null);
  }, [open, entry, date]);

  // The first thing somebody does is say what the time was against, so the cursor starts there.
  useEffect(() => {
    if (open && !editing) {
      const focus = window.setTimeout(() => searchBox.current?.focus(), 60);
      return () => window.clearTimeout(focus);
    }
    return undefined;
  }, [open, editing]);

  const chooseSuggestion = (suggestion: WorkSuggestion) => {
    setWorkItemId(suggestion.workItemId);
    setTarget(suggestion.projectId);
    setPickedLabel(`${suggestion.itemKey} · ${suggestion.title}`);
    setSearch("");
    setError(null);
  };

  const clearPick = () => {
    setWorkItemId(null);
    setPickedLabel(null);
    setTarget(INTERNAL);
  };

  const submit = async () => {
    if (minutes == null || minutes <= 0) {
      setError("Enter how long, for example 2h 30m.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (entry) {
        await api.patch(`/api/v1/worklogs/${entry.id}`, {
          minutes,
          description: description || null,
          workDate,
          version: entry.version,
        });
      } else {
        await api.post("/api/v1/worklogs", {
          workItemId,
          projectId: workItemId || target === INTERNAL || target === "" ? null : target,
          workDate,
          minutes,
          description: description || null,
        });
      }
      onSaved();

      if (logAnother && !editing) {
        // Keep the date and what it was against — the next entry is usually the same day, and
        // often the same ticket at a different length. Only the duration and note reset.
        setDuration("");
        setDescription("");
        setError(null);
      } else {
        onClose();
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that entry.");
    } finally {
      setSaving(false);
    }
  };

  /*
   * Ctrl/Cmd + Enter submits.
   *
   * Somebody entering six lines of a week never leaves the keyboard, and reaching for the mouse
   * once per entry is most of what makes a timesheet feel slow.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && !saving) {
      event.preventDefault();
      void submit();
    }
  };

  const internal = !workItemId && (target === INTERNAL || target === "");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Correct this entry" : "Log time"}
      subtitle={workDate ? formatDayLong(workDate) : undefined}
      width="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          {editing ? (
            <span />
          ) : (
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={logAnother}
                onChange={(event) => setLogAnother(event.target.checked)}
                className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-200"
              />
              Log another
            </label>
          )}
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-ink-500 sm:inline">⌘/Ctrl + Enter</span>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? "Saving…" : editing ? "Save correction" : "Log time"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4" onKeyDown={onKeyDown}>
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!editing && (
          pickedLabel ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2.5">
              <span className="min-w-0">
                <span className="block text-xs text-brand-700">Logging against</span>
                <span className="block truncate text-sm font-medium text-ink-900">
                  {pickedLabel}
                </span>
              </span>
              <button
                type="button"
                onClick={clearPick}
                aria-label="Clear the selected work item"
                className="shrink-0 rounded-md p-1 text-ink-500 transition-colors hover:bg-surface hover:text-ink-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3">
              <Field
                label="Work item"
                hint={
                  debounced
                    ? "Searching every open item across all projects."
                    : "Search, or pick from what you have been working on. Hours are never filled in for you."
                }
              >
                <span className="relative block">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                  <Input
                    ref={searchBox}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search by key or title, e.g. MB-2 or payment"
                    className="pl-8"
                  />
                </span>
              </Field>

              <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
                {suggestions.isFetching && (
                  <p className="px-2.5 py-2 text-xs text-ink-500">Looking…</p>
                )}

                {!suggestions.isFetching
                  && debounced
                  && (suggestions.data?.length ?? 0) === 0 && (
                  <p className="px-2.5 py-2 text-xs text-ink-600">
                    Nothing open matches “{debounced}”. Closed work is left out of this list — log
                    it against the project below instead.
                  </p>
                )}

                {!debounced && (suggestions.data?.length ?? 0) > 0 && (
                  <p className="flex items-center gap-2 px-1 pb-1 text-xs font-medium text-ink-600">
                    <Lightbulb className="h-3.5 w-3.5" />
                    Work you might have spent time on
                  </p>
                )}

                {suggestions.data?.map((suggestion) => (
                  <button
                    key={suggestion.workItemId}
                    type="button"
                    onClick={() => chooseSuggestion(suggestion)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-2",
                      "text-left text-sm transition-colors hover:bg-surface",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-ink-500">{suggestion.itemKey}</span>{" "}
                      <span className="text-ink-800">{suggestion.title}</span>
                    </span>
                    <span className="shrink-0 text-xs text-ink-500">
                      {debounced ? suggestion.projectKey : suggestion.reason}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" required>
            <Input
              type="date"
              value={workDate}
              onChange={(event) => setWorkDate(event.target.value)}
            />
          </Field>
          <Field
            label="How long"
            required
            hint={
              minutes != null
                ? `Understood as ${formatMinutes(minutes)}`
                : "2h 30m, 2:30, 150m or 2.5"
            }
            error={duration !== "" && minutes == null ? "Not a duration this can read." : undefined}
          >
            <Input
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
              placeholder="2h 30m"
            />
          </Field>
        </div>

        {editing ? (
          <div className="rounded-xl border border-ink-200 px-3 py-2.5 text-sm">
            <p className="text-xs text-ink-500">Logged against</p>
            <p className="mt-0.5 text-ink-800">
              {entry?.workItemKey
                ? `${entry.workItemKey} · ${entry.workItemTitle ?? ""}`
                : (entry?.projectName ?? "Internal — no project")}
            </p>
            <p className="mt-1 text-xs text-ink-400">
              What time was spent on cannot be changed here. Delete the entry and log it again
              against the right thing, so the correction is visible rather than silent.
            </p>
          </div>
        ) : workItemId ? null : (
          <Field
            label="Against"
            hint="Leave as internal for time that belongs to no project — a meeting, admin, training."
          >
            <Select value={target} onChange={(event) => setTarget(event.target.value)}>
              <option value={INTERNAL}>Internal — no project</option>
              {projects.data?.content.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectKey} · {project.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field
          label="What was it"
          required={internal}
          hint={
            internal
              ? "Time against no project is explained by these words alone, so they are required."
              : "Optional. Useful when the ticket title does not say what the hours went on."
          }
        >
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={internal ? "Sprint planning" : ""}
          />
        </Field>
      </div>
    </Drawer>
  );
}
